import React, { useEffect, useState } from "react";
import { Row, Col, Attachment, Button, Slider, Alerts, Loader } from "@kaleyra/emmy";
import Styles from './index.module.scss';
import { useTranslation } from "react-i18next";
import cn from 'classnames'
import Approved from '../../../../../../assets/approved-neutral.svg';
import Timestamp from '../../../../../../assets/timestamp_fill.svg';
import RejectedIcon from '../../../../../../assets/rejected.svg';
import { Steps, Step } from '@kaleyra/emmy';
import { timelineType } from "../../../../../../interface";
import { isEmpty } from "lodash";
import { checkViewOnlyPrivileges, dateToStringFormat } from "../../../../../../utils";

export type MapStatePropsType = {
    params: any;
    senderIdDetails: any;
    inProgress: boolean;
    userProfile: any;
};

export type DispatchPropsType = {
    onClose: () => void;
    onEdit: (param: any) => void;
    onLoadDataById: (param: any) => void;
};


enum tabInstances {
    Details = 'details',
    Timeline = 'timeline'
}

type tabPropTypes = tabInstances.Details | tabInstances.Timeline;


const SenderIdView: React.FC<DispatchPropsType & MapStatePropsType> = ({
    onClose,
    onEdit,
    params,
    onLoadDataById,
    senderIdDetails,
    inProgress,
    userProfile
}) => {
    const [selectedTab, setSelectedTab] = useState<tabPropTypes>(tabInstances.Details);

    const { t } = useTranslation();
    const { country_id } = userProfile.fullProfile;


    const isIndianUser = country_id.toString() === "277";

    const tabs = [{
        label: t('KLR00100'),
        value: tabInstances.Details,
    }];

    if(senderIdDetails.data && senderIdDetails.data?.senders[0]?.type?.toUpperCase() != "N"){
        tabs.push({
            label: t('KLR00101'),
            value: tabInstances.Timeline
        });
    }

    useEffect(() => {
        onLoadDataById(params);
    }, []);

    const getDocumentDetails = (docId: string) => {
        const doc_details = senderIdDetails?.data?.doc_details;
        return doc_details.filter((docItem: any) => (docItem.id).toString() === docId)[0];
    }

    const getAlertType = (status: string) => {
        if (status.toString() === "2") {
            return "warning";
        } else if (status.toString() === "3" || status.toString() === "4") {
            return "error";
        } else return "success";
    }

    const renderTimelineIcon = (action: string) => {
        switch (action) {
            case "Under Verification":
                return <img src={Timestamp} alt="under verification" />;
            case "Approved":
                return <img src={Approved} alt="approved" />;
            default:
                return <img src={RejectedIcon} alt="rejected" />;
        }
    }

    const getBody = () => {
        const senders = senderIdDetails?.data?.senders[0];
        const timeline = senderIdDetails?.data?.activity || [];
        const senderType = senders?.type || "";

        if (selectedTab === tabInstances.Details) {
            return <>
                <div className={cn(Styles.editBtnSection, Styles.flexEnd)}>
                    {(senders.status.id).toString() !== "3" && 
                        !Boolean(senders.is_default) && 
                        !checkViewOnlyPrivileges('sms', 'senderId') && 
                        senderType.toUpperCase() != "N" &&
                            <Button type="secondary" className={Styles.editBtn} onClick={() => onEdit(params)}>{t('mainEdit')}</Button>
                    }
                </div>
                <div>
                    <p className={Styles.subheading}>{t('basicInformation')}</p>
                    <div className={Styles.infoContainer}>
                        <Row>
                            <Col size={6}>
                                <p className={Styles.infoTitle}>{t('KLR00102')}</p>
                            </Col>
                            <Col size={6}>
                                <p className={Styles.infoValue}>{senders.sender || ''}</p>
                            </Col>
                        </Row>
                        <Row>
                            <Col size={6}>
                                <p className={Styles.infoTitle}>{t('mainCountry')}</p>
                            </Col>
                            <Col size={6}>
                                <p className={Styles.infoValue}>{senders.country_details?.name || '-'}</p>
                            </Col>
                        </Row>
                        <Row>
                            <Col size={6}>
                                <p className={Styles.infoTitle}>{t('BLD00607')}</p>
                            </Col>
                            <Col size={6}>
                                <p className={Styles.infoValue}>{senders.description || '-'}</p>
                            </Col>
                        </Row>
                        {isIndianUser &&
                            <Row>
                                <Col size={6}>
                                    <p className={Styles.infoTitle}>{t('verifyEntityId')}</p>
                                </Col>
                                <Col size={6}>
                                    <p className={Styles.infoValue}>{!isEmpty(senders.entity_id) ? senders.entity_id : senderIdDetails?.default_entity_id}</p>
                                </Col>
                            </Row>
                        }
                        {senders.vsms_sender_mapping && senders.vsms_status !== "0" && <Row>
                            <Col size={6}>
                                <p className={Styles.infoTitle}>{t('BLD01635')}</p>
                            </Col>
                            <Col size={6}>
                                <p className={Styles.infoValue}>{JSON.parse(senders.vsms_sender_mapping.route).toString()}</p>
                            </Col>
                        </Row>}
                    </div>
                </div>
                <div>
                    {senders.document_ids?.length > 0 &&
                        <p className={Styles.subheading}>{t('uploadedDoc')}</p>
                    }
                    {
                        senders.document_ids?.map((docId: string, index: number) => (
                            <div className={Styles.attachment}>
                                <Attachment
                                    key={index}
                                    fileName={getDocumentDetails(docId).title || ''}
                                    fileType={getDocumentDetails(docId).title || ''}
                                    fileUrl={getDocumentDetails(docId).url || ''}
                                    id={docId}
                                    closable={false}
                                    showImage={false}
                                    download={true} />
                            </div>
                        ))
                    }
                </div>
            </>
        }
        else {
            return <>
                <div className={Styles.alertWrapper}>
                    <Alerts className={Styles.alert} type={getAlertType(senders.status.id)} content={`Sender ID ${senders.status.label}`} showIcon={false} />
                </div>
                <div className={Styles.subheadingWrapper}>
                    <p className={Styles.subheading}>Sender ID timeline</p>
                </div>
                <Steps direction="vertical" current={timeline.length} type="default">
                    {
                        timeline.map((listItem: timelineType, index: number) => {
                            let createdAt = new Date(listItem.created_at);
                            const modifiedDate = `On ${dateToStringFormat(createdAt)}`;
                            return (
                                <Step key={index}
                                    className={Styles.step}
                                    title={listItem.comments}
                                    description={modifiedDate}
                                    icon={renderTimelineIcon(listItem.action)} />)
                        }
                        )
                    }
                </Steps>
            </>
        }
    }

    return (
        <Slider
            containerId="root-modal"
            onClose={onClose}
            tabs={tabs}
            onTabClick={(selectedTab) => setSelectedTab(selectedTab as tabPropTypes)}
            initActiveTab="details"
            visibility={true}
            title={t('smsSenderIdDetails')}
        >
            <div className={Styles.container}>
                {inProgress || isEmpty(senderIdDetails?.data) ? <Loader /> :
                    getBody()}
            </div>
        </Slider >
    );


}

export default SenderIdView;